package at.theatervogelweide.admin.web.screens.board

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.AdminError
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.ErrorCode
import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.ImageCategory
import at.theatervogelweide.admin.contract.VersionedId
import at.theatervogelweide.admin.contract.board.BoardMemberDraft
import at.theatervogelweide.admin.contract.board.BoardMemberEntry
import at.theatervogelweide.admin.contract.board.BoardService
import at.theatervogelweide.admin.contract.moved
import at.theatervogelweide.admin.contract.people.PeopleService
import at.theatervogelweide.admin.contract.people.PersonListEntry
import at.theatervogelweide.admin.contract.people.initials
import at.theatervogelweide.admin.web.AdminApi
import at.theatervogelweide.admin.web.Route
import at.theatervogelweide.admin.web.components.AdminAvatar
import at.theatervogelweide.admin.web.components.AdminButton
import at.theatervogelweide.admin.web.components.AdminCard
import at.theatervogelweide.admin.web.components.AdminCardFooterSpacer
import at.theatervogelweide.admin.web.components.AdminConfirmDialog
import at.theatervogelweide.admin.web.components.AdminConflictCallout
import at.theatervogelweide.admin.web.components.AdminPageHeader
import at.theatervogelweide.admin.web.components.AdminPersonPicker
import at.theatervogelweide.admin.web.components.AdminSelectableList
import at.theatervogelweide.admin.web.components.AdminSplitView
import at.theatervogelweide.admin.web.components.AdminTextField
import at.theatervogelweide.admin.web.components.Bem
import at.theatervogelweide.admin.web.components.ButtonVariant
import at.theatervogelweide.admin.web.components.LocalPanes
import at.theatervogelweide.admin.web.components.PublicSiteLink
import at.theatervogelweide.admin.web.components.ReorderAnnouncement
import at.theatervogelweide.admin.web.components.ReportUnsavedChanges
import at.theatervogelweide.admin.web.components.SelectableRow
import at.theatervogelweide.admin.web.components.SplitController
import at.theatervogelweide.admin.web.components.UnsavedChangesGuard
import at.theatervogelweide.admin.web.components.editorShown
import at.theatervogelweide.admin.web.components.rememberReorder
import at.theatervogelweide.admin.web.rpcCall
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.NonCancellable
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.withContext
import kotlinx.rpc.RpcClient
import kotlinx.rpc.withService
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private val card = Bem("admin-card")
private val button = Bem("admin-button")
private val listPanel = Bem("admin-list-panel")
private val alert = Bem("admin-alert")
private val fieldset = Bem("admin-fieldset")
private val boardPerson = Bem("admin-board-person")

/** The editor of one member, or of a new one while [loaded] is `null`. */
private class BoardEditorState(loaded: BoardMemberEntry?) {
    var loaded: BoardMemberEntry? by mutableStateOf(loaded)
    val original: BoardMemberDraft = loaded?.draft ?: BoardMemberDraft()

    var form: BoardMemberDraft by mutableStateOf(original)
    var fieldErrors: List<FieldError> by mutableStateOf(emptyList())
    var error: String? by mutableStateOf(null)
    var conflict: Boolean by mutableStateOf(false)
    var saving: Boolean by mutableStateOf(false)
    var reloading: Boolean by mutableStateOf(false)

    val busy: Boolean get() = saving || reloading

    val isNew: Boolean get() = loaded == null

    val hasChanges: Boolean get() = form != original

    fun errorFor(name: String): String? = fieldErrors.firstOrNull { it.field == name }?.message

    fun showFailure(failure: AdminError) {
        conflict = failure.code == ErrorCode.CONFLICT
        fieldErrors = failure.fieldErrors
        error = when {
            conflict || failure.fieldErrors.isNotEmpty() -> null
            failure.code == ErrorCode.NOT_FOUND -> "Dieses Vorstandsmitglied gibt es nicht mehr. Bitte wähle ein anderes."
            else -> failure.message
        }
    }
}

/**
 * A moved order to send; [number] counts the moves, so moving back to an order sent before is a request of its own
 * and isn't merged with that one.
 */
private data class ReorderRequest(val number: Int, val order: List<Long>)

/** Runs what a screen still sends after it was left, such as its last reorder; the RPC connection outlives screens. */
private val leftScreens = MainScope()

/**
 * Vorstand (#23): the board in the public site's order beside the editor of the selected member. People are chosen
 * from Personen, whose photos the public site shows; [onOpenPerson] opens one there. [selectedId] comes from the
 * address, and [split] changes it; with panes and no selection the first member opens. [startNew] opens the form for
 * a new member (`/vorstand/neu`).
 */
@OptIn(FlowPreview::class)
@Composable
fun BoardScreen(
    rpc: RpcClient,
    api: AdminApi,
    guard: UnsavedChangesGuard,
    split: SplitController,
    selectedId: Long?,
    startNew: Boolean,
    onOpenPerson: (Long) -> Unit,
) {
    val service = remember(rpc) { rpc.withService<BoardService>() }
    val peopleService = remember(rpc) { rpc.withService<PeopleService>() }
    val scope = rememberCoroutineScope()

    var entries by remember { mutableStateOf<List<BoardMemberEntry>?>(null) }
    // the order shown, which runs ahead of the server's while a reorder is on its way
    var order by remember { mutableStateOf(emptyList<Long>()) }
    var people by remember { mutableStateOf<List<PersonListEntry>?>(null) }
    var peopleError by remember { mutableStateOf<String?>(null) }
    var listError by remember { mutableStateOf<String?>(null) }
    var editor by remember { mutableStateOf<BoardEditorState?>(null) }
    var editorError by remember { mutableStateOf<String?>(null) }
    var creating by remember { mutableStateOf(startNew) }
    var confirmDelete by remember { mutableStateOf(false) }
    val reorderRequests = remember { MutableStateFlow<ReorderRequest?>(null) }
    // a moved order the server hasn't confirmed yet; list reloads keep it instead of the order they read
    var reorderPending by remember { mutableStateOf(false) }
    // a moved order still waiting out the debounce; leaving the screen sends it instead of dropping it
    val unsentReorder = remember { mutableStateOf(false) }
    val reorderLock = remember { Mutex() }
    // moves up to this number were dropped with "Verwerfen und wechseln" and are no longer sent
    val discardedMoves = remember { mutableStateOf(0) }

    suspend fun reloadList() {
        when (val result = rpcCall { service.list() }) {
            is AdminResult.Success -> {
                entries = result.value
                val ids = result.value.map { it.id }
                order = if (reorderPending) order.filter { it in ids } + ids.filterNot { it in order } else ids
                listError = null
            }

            is AdminResult.Failure -> listError = result.error.message
        }
    }

    // counts the people requests, so an older one that finishes late doesn't overwrite a newer one's answer
    val peopleRequests = remember { mutableStateOf(0) }

    suspend fun reloadPeople() {
        val request = ++peopleRequests.value
        val result = rpcCall { peopleService.list() }
        if (request != peopleRequests.value) {
            return
        }
        when (result) {
            is AdminResult.Success -> {
                people = result.value
                peopleError = null
            }

            // the person picker says so and offers to try again
            is AdminResult.Failure -> peopleError = result.error.message
        }
    }

    LaunchedEffect(service) {
        reloadList()
        reloadPeople()
    }

    // the form for a new member follows the address: /vorstand/neu opens it, leaving it for /vorstand closes it
    LaunchedEffect(startNew) {
        when {
            startNew && editor?.isNew != true -> {
                creating = true
                editorError = null
                editor = BoardEditorState(null)
            }

            !startNew && creating && selectedId == null -> {
                editor = null
                creating = false
            }
        }
    }

    // back on the list alone, the editor and its changes are gone
    val editorVisible = editorShown(selectedId, startNew)
    LaunchedEffect(editorVisible) {
        if (!editorVisible) {
            editor = null
            editorError = null
            creating = false
        }
    }

    // the list holds every member with all the editor needs, so selecting needs no request; with panes and without a
    // selection, the first member opens
    val panes = LocalPanes.current
    LaunchedEffect(entries, selectedId, creating, panes) {
        val all = entries ?: return@LaunchedEffect
        when {
            creating -> Unit
            selectedId == null -> if (panes) all.firstOrNull()?.let { split.show(it.id) }
            editor?.loaded?.id != selectedId -> {
                val entry = all.firstOrNull { it.id == selectedId }
                editor = entry?.let { BoardEditorState(it) }
                editorError = if (entry == null) "Dieses Vorstandsmitglied gibt es nicht mehr." else null
            }
        }
    }

    /** Sends [request] with the versions of the latest list and takes in the answer. */
    suspend fun sendReorder(request: ReorderRequest) {
        if (request.number <= discardedMoves.value) {
            return
        }
        val requested = request.order
        val versions = entries.orEmpty().associate { it.id to it.version }
        // a member added or removed since the move: the server needs exactly the current members, new ones at the end
        val members = requested.filter { it in versions } + versions.keys.filterNot { it in requested }
        val result = rpcCall { service.reorder(members.map { VersionedId(it, versions.getValue(it)) }) }
        // a later move is waiting: the shown order stays ahead of the server's until that one is answered too
        val latest = reorderRequests.value?.number ?: 0
        val laterMove = latest != request.number && latest > discardedMoves.value
        when (result) {
            is AdminResult.Success -> {
                entries = result.value
                if (!laterMove) {
                    order = result.value.map { it.id }
                }
                reorderPending = laterMove
                listError = null
                // the open member moved too; its new version is the one to save against
                editor?.let { state ->
                    val loaded = state.loaded ?: return@let
                    val after = result.value.firstOrNull { it.id == loaded.id }?.version ?: return@let
                    if (versions[loaded.id] == loaded.version) {
                        state.loaded = loaded.copy(version = after)
                    }
                }
            }

            is AdminResult.Failure -> {
                val message = if (result.error.code == ErrorCode.CONFLICT) {
                    "Die Liste hat sich inzwischen geändert. Sie ist jetzt neu geladen; bitte verschiebe noch einmal."
                } else {
                    "Die Reihenfolge konnte nicht gespeichert werden. ${result.error.message}"
                }
                reorderPending = laterMove
                reloadList()
                // the reload succeeding doesn't make the move's failure go away
                listError = message
            }
        }
    }

    /**
     * Runs a change of a member behind the reorders: a moved order still waiting is sent first, so the change starts
     * from the versions it answered with and neither makes the other a conflict.
     */
    suspend fun <T> afterReorders(change: suspend () -> T): T = reorderLock.withLock {
        val waiting = reorderRequests.value
        if (unsentReorder.value && waiting != null) {
            unsentReorder.value = false
            sendReorder(waiting)
        }
        change()
    }

    // reorders are sent one after another, each with the versions of the latest list; leaving the screen neither takes
    // back one on its way nor lets the last one overtake it
    LaunchedEffect(service) {
        reorderRequests.filterNotNull().debounce(400).collect { requested ->
            unsentReorder.value = false
            withContext(NonCancellable) { reorderLock.withLock { sendReorder(requested) } }
        }
    }

    DisposableEffect(service) {
        onDispose {
            val requested = reorderRequests.value
            if (unsentReorder.value && requested != null) {
                leftScreens.launch { reorderLock.withLock { sendReorder(requested) } }
            }
        }
    }

    val state = editor.takeIf { editorVisible }
    val hasChanges = state?.hasChanges == true
    // a move not sent yet counts too, so signing out or closing the tab asks first; one already on its way is being
    // saved, not unsaved, and lands whatever the person answers
    ReportUnsavedChanges(guard, hasChanges || unsentReorder.value) {
        // "Verwerfen und wechseln": the moves not sent yet are dropped and the list shows the server's order again,
        // until the answer to a move on its way brings in what it stored
        discardedMoves.value = reorderRequests.value?.number ?: 0
        if (unsentReorder.value) {
            unsentReorder.value = false
            reorderPending = false
            order = entries.orEmpty().map { it.id }
        }
    }

    fun select(id: Long) {
        if (id == selectedId && !creating) {
            return
        }
        guard.proceed {
            creating = false
            if (editor?.loaded?.id != id) {
                editor = null
            }
            split.open(id)
        }
    }

    fun startNew() {
        guard.proceed {
            creating = true
            editorError = null
            editor = BoardEditorState(null)
            split.openNew()
        }
    }

    fun save() {
        val current = editor ?: return
        val draft = current.form.copy(function = current.form.function.trim())
        val errors = draft.validate()
        current.fieldErrors = errors
        current.error = null
        if (errors.isNotEmpty() || current.saving) {
            return
        }

        current.saving = true
        scope.launch {
            val result = afterReorders {
                // read only now: a reorder sent just before may have given the member a new version
                val loaded = current.loaded
                rpcCall { if (loaded == null) service.create(draft) else service.save(loaded.versionedId, draft) }
                    // the next reorder needs the new versions
                    .also { if (it is AdminResult.Success) reloadList() }
            }
            current.saving = false
            // the person may have switched to another member meanwhile; the list shows the result already
            if (editor !== current) {
                return@launch
            }
            when (result) {
                is AdminResult.Success -> {
                    editor = BoardEditorState(result.value)
                    creating = false
                    split.show(result.value.id)
                }

                is AdminResult.Failure -> {
                    current.showFailure(result.error)
                    // the chosen person may have been deleted meanwhile
                    if (result.error.fieldErrors.any { it.field == BoardMemberDraft.PERSON }) {
                        reloadPeople()
                    }
                }
            }
        }
    }

    fun discard() {
        val current = editor ?: return
        val loaded = current.loaded
        if (loaded == null) {
            editor = null
            creating = false
            split.backToList()
        } else {
            editor = BoardEditorState(loaded)
        }
    }

    fun reloadAfterConflict() {
        val current = editor ?: return
        if (current.reloading) {
            return
        }
        current.reloading = true
        scope.launch {
            try {
                reloadList()
                reloadPeople()
            } finally {
                current.reloading = false
            }
            if (editor === current) {
                val id = current.loaded?.id
                val entry = entries?.firstOrNull { it.id == id }
                editor = entry?.let { BoardEditorState(it) }
                editorError = if (entry == null) "Dieses Vorstandsmitglied gibt es nicht mehr." else null
            }
        }
    }

    fun delete() {
        confirmDelete = false
        val current = editor ?: return
        val loaded = current.loaded ?: return
        scope.launch {
            val result = afterReorders {
                rpcCall { service.delete((current.loaded ?: loaded).versionedId) }.also { if (it is AdminResult.Success) reloadList() }
            }
            // the person may have switched to another member meanwhile; the list shows the result already
            if (editor !== current) {
                return@launch
            }
            when (result) {
                is AdminResult.Success -> {
                    editor = null
                    entries = entries?.filterNot { it.id == loaded.id }
                    order = order - loaded.id
                    split.backToList()
                }

                is AdminResult.Failure -> current.showFailure(result.error)
            }
        }
    }

    AdminPageHeader("Vorstand", unsavedChanges = hasChanges) {
        AdminButton(variant = ButtonVariant.PRIMARY, onClick = ::startNew) { Text("+ Vorstandsmitglied") }
    }

    val byId = entries.orEmpty().associateBy { it.id }
    val ordered = order.mapNotNull { byId[it] }
    val reorder = rememberReorder(
        keys = ordered.map { it.id },
        describe = { id -> byId[id]?.name ?: "Vorstandsmitglied" },
    ) { from, to ->
        order = order.moved(from, to)
        reorderPending = true
        unsentReorder.value = true
        reorderRequests.value = ReorderRequest((reorderRequests.value?.number ?: 0) + 1, order)
    }

    AdminSplitView(
        controller = split,
        selectedId = selectedId,
        new = startNew,
        name = "Vorstand",
        listLabel = "Liste des Vorstands",
        list = {
            listError?.let {
                P({
                    classes(listPanel.element("message").className, alert.block)
                    attr("role", "alert")
                }) { Text(it) }
            }

            when {
                entries == null -> P({ classes(listPanel.element("message").className) }) { Text("Lade …") }
                ordered.isEmpty() -> P({ classes(listPanel.element("message").className) }) {
                    Text("Noch niemand im Vorstand. Füge mit „+ Vorstandsmitglied“ die erste Person hinzu.")
                }

                else -> AdminSelectableList(
                    rows = ordered.map { member ->
                        SelectableRow(
                            key = member.id,
                            title = member.name,
                            meta = member.function,
                            thumbnailUrl = member.imageUuid?.let { api.imageUrl(ImageCategory.PERSONS, it) },
                        )
                        },
                    selected = if (creating) null else selectedId,
                    onSelect = ::select,
                    recent = split.recent,
                    reorder = reorder.takeIf { ordered.size > 1 },
                )
            }
            ReorderAnnouncement(reorder)
        },
        detail = {
            when {
                // a new editor gets new fields, without the search typed into the previous one
                state != null -> key(state) {
                    BoardEditorCard(
                        state = state,
                        api = api,
                        people = people,
                        peopleError = peopleError,
                        onRetryPeople = { scope.launch { reloadPeople() } },
                        onOpenPerson = onOpenPerson,
                        onSave = ::save,
                        onDiscard = ::discard,
                        onDelete = { confirmDelete = true },
                        onReload = ::reloadAfterConflict,
                    )
                }

                editorError != null -> P({
                    classes(alert.block)
                    attr("role", "alert")
                }) { Text(editorError!!) }
            }
        },
    )

    AdminConfirmDialog(
        open = confirmDelete,
        id = "vorstand-entfernen",
        title = "Aus dem Vorstand entfernen?",
        message = "„${state?.loaded?.name.orEmpty()}“ verschwindet von der Vorstandsseite der Website. " +
            "Die Person bleibt unter Personen erhalten.",
        confirmLabel = "Entfernen",
        cancelLabel = "Behalten",
        onConfirm = ::delete,
        onCancel = { confirmDelete = false },
        danger = true,
    )
}

@Composable
private fun BoardEditorCard(
    state: BoardEditorState,
    api: AdminApi,
    people: List<PersonListEntry>?,
    peopleError: String?,
    onRetryPeople: () -> Unit,
    onOpenPerson: (Long) -> Unit,
    onSave: () -> Unit,
    onDiscard: () -> Unit,
    onDelete: () -> Unit,
    onReload: () -> Unit,
) {
    val form = state.form

    Form(attrs = {
        attr("novalidate", "")
        onSubmit { event ->
            event.preventDefault()
            onSave()
        }
    }) {
        AdminCard(
            title = if (state.isNew) "Neues Vorstandsmitglied" else "Vorstandsmitglied bearbeiten",
            id = "vorstandsmitglied",
            footer = if (state.isNew) {
                null
            } else {
                {
                    AdminButton(variant = ButtonVariant.DANGER, disabled = state.busy, onClick = onDelete) { Text("Entfernen") }
                    AdminCardFooterSpacer()
                    PublicSiteLink("/about-us/board-members", button.block, button.modifier("link")) { Text("Auf Website ansehen ↗") }
                }
            },
            saveActions = {
                AdminButton(disabled = state.busy || (!state.hasChanges && !state.isNew), onClick = onDiscard) { Text("Verwerfen") }
                AdminButton(
                    variant = ButtonVariant.PRIMARY,
                    type = ButtonType.Submit,
                    disabled = state.busy || (!state.hasChanges && !state.isNew),
                ) { Text(if (state.saving) "Speichert …" else "Speichern") }
            },
        ) {
            if (state.conflict) {
                AdminConflictCallout(onReload = onReload)
            }
            state.error?.let {
                P({
                    classes(alert.block)
                    attr("role", "alert")
                }) { Text(it) }
            }

            Fieldset({
                classes(fieldset.block)
                if (state.busy) {
                    attr("disabled", "")
                }
            }) {
                AdminPersonPicker(
                    id = "board-${BoardMemberDraft.PERSON}",
                    personId = form.personId,
                    people = people,
                    onChange = { state.form = state.form.copy(personId = it) },
                    hint = "Fehlt die Person? Lege sie zuerst unter Personen an.",
                    error = state.errorFor(BoardMemberDraft.PERSON),
                    // a member whose person is missing from the loaded list still shows its name
                    fallbackName = state.loaded?.takeIf { it.personId == form.personId }?.name,
                    loadError = peopleError,
                    onRetry = onRetryPeople,
                )
                AdminTextField(
                    id = "board-${BoardMemberDraft.FUNCTION}",
                    label = "Funktion",
                    value = form.function,
                    onValueChange = { state.form = state.form.copy(function = it) },
                    error = state.errorFor(BoardMemberDraft.FUNCTION),
                    hint = "Wie sie auf der Website steht, z. B. „Kassa“ oder „Schriftführung“.",
                    autocomplete = "off",
                )
            }

            val person = people?.firstOrNull { it.id == form.personId }
            if (person != null) {
                Div({ classes(boardPerson.block) }) {
                    AdminAvatar(
                        initials(person.firstName, person.lastName),
                        person.imageUuid?.let { api.imageUrl(ImageCategory.PERSONS, it) },
                        large = true,
                    )
                    Span({ classes(card.element("hint").className) }) {
                        Text("Foto und Name werden unter Personen verwaltet: ")
                        A(Route.People(person.id).path, {
                            onClick { event ->
                                if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
                                    event.preventDefault()
                                    onOpenPerson(person.id)
                                }
                            }
                        }) { Text(person.name) }
                    }
                }
            }
        }
    }
}
